import { useContext, useMemo } from 'react'
import { RosterSetupContext } from '../RosterSetup'
import { useTeam } from 'app/frontend/base/config/context/team'
import ProtectedContent from 'app/frontend/base/components/ProtectedContent'
import { Button } from '@/components/ui/button'
import { useNavigate } from 'react-router-dom'
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card'
import { Info, TriangleAlert } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { Badge } from '@/components/ui/badge'
import { CharacterClassIcon } from 'app/frontend/base/components'
import Card from 'app/frontend/base/components/Card'

type ExcludedTeamRanksHoverCardProps = {
  excludedTeamRanks: wowaudit.TeamRank[]
}

export const ExcludedTeamRanksHoverCard = ({
  excludedTeamRanks,
}: ExcludedTeamRanksHoverCardProps) => {
  const { team } = useTeam()
  const navigate = useNavigate()

  return (
    <HoverCard openDelay={150} closeDelay={150}>
      <HoverCardTrigger className='cursor-pointer'>
        <Info size={14} className='opacity-70' />
      </HoverCardTrigger>
      <HoverCardContent className='w-72'>
        <p>
          Team members of the following ranks are excluded from the roster
          setup:
        </p>

        <ul className='list-disc list-inside'>
          {excludedTeamRanks.map((r) => (
            <li key={r.id}>{r.name}</li>
          ))}
        </ul>

        <ProtectedContent
          section='roster'
          requirement='admin'
          renderUnauthorized={false}
        >
          <Button
            variant='secondary'
            size='xs'
            className='mt-3'
            onClick={() => navigate(`${team.path}/settings#roster`)}
          >
            Manage team ranks
          </Button>
        </ProtectedContent>
      </HoverCardContent>
    </HoverCard>
  )
}

const TotalCharacters = ({ className }: { className?: string }) => {
  const { team } = useTeam()
  const { mainsByRole, altsByRole } = useContext(RosterSetupContext)

  const excludedTeamRanks = useMemo(
    () => team.teamRanks?.filter((r) => !r.roster_visibility),
    [team.teamRanks],
  )
  const excludedTeamRankIds = excludedTeamRanks?.map((r) => r.id)

  const excludedMemberCount = useMemo(
    () =>
      team.members?.filter((m) => excludedTeamRankIds?.includes(m.teamRank.id))
        ?.length ?? 0,
    [team.members],
  )

  const mainCount = Object.values(mainsByRole).reduce(
    (acc, role) => acc + (role?.length ?? 0),
    0,
  )
  const altCount = Object.values(altsByRole).reduce(
    (acc, role) => acc + (role?.length ?? 0),
    0,
  )

  const charactersWithProblems = team.members?.filter(
    (m) => m.characterReference.gdprStatus !== 'exists',
  )

  return (
    <Card className={cn('w-full', className)} size='sm'>
      <div className='flex items-center justify-between gap-2'>
        <div className='flex items-center gap-2'>
          <span className='font-semibold text-lg leading-none'>
            {mainCount + altCount}
          </span>
          <span>character{mainCount + altCount === 1 ? '' : 's'} in setup</span>
        </div>
        {charactersWithProblems && charactersWithProblems.length > 0 && (
          <Tooltip>
            <TooltipTrigger>
              <Badge
                variant='outline'
                className='flex items-center gap-1.5 text-base'
              >
                <TriangleAlert size={16} className='text-warning-foreground' />{' '}
                {charactersWithProblems.length}
              </Badge>
            </TooltipTrigger>
            <TooltipContent>
              <p className='text-xs font-normal border-b mb-1'>
                Unavailable characters
              </p>
              {charactersWithProblems.map((m) => (
                <div
                  key={m.id}
                  className='font-normal flex items-center gap-1.5 mb-1'
                >
                  <CharacterClassIcon
                    characterClass={m.characterReference.characterClass}
                    size='small'
                  />
                  {m.characterReference.name}
                </div>
              ))}
            </TooltipContent>
          </Tooltip>
        )}
      </div>

      {excludedMemberCount > 0 ? (
        <div className='flex items-center gap-2'>
          <span className='text-muted-foreground text-xs'>
            + {excludedMemberCount} excluded
          </span>
          <ExcludedTeamRanksHoverCard
            excludedTeamRanks={excludedTeamRanks ?? []}
          />
        </div>
      ) : null}
    </Card>
  )
}

export default TotalCharacters
